import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Bell, Mail, Clock, Send, Sunrise, Moon, ALargeSmall, RotateCcw, Clock12, Megaphone } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useTextSize } from "@/context/TextSizeContext";
import { enablePush, disablePush, pushSupported } from "@/lib/push";
import { Loader } from "@/components/Loader";
import { PushHealth } from "@/components/PushHealth";
import { TopLogoBar, BottomTabs } from "@/components/AppShell";

const MOMENTS = [
  { key: "mattino", label: "Mattino", icon: Sunrise },
  { key: "focus", label: "Mezzogiorno", icon: Clock12 },
  { key: "sera", label: "Sera", icon: Moon },
];

function Toggle({ checked, onChange, testid }) {
  return (
    <button
      onClick={() => onChange(!checked)}
      data-testid={testid}
      role="switch"
      aria-checked={!!checked}
      className="relative h-7 w-12 rounded-full transition"
      style={{ background: checked ? "linear-gradient(135deg,#E5C158,#997A20)" : "#27272A" }}
    >
      <span className={`absolute top-0.5 h-6 w-6 rounded-full bg-[#FAF9F6] shadow transition-all ${checked ?"left-[22px]" : "left-0.5"}`} />
    </button>
  );
}

export default function Settings() {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const { big, setSize } = useTextSize();
  const [prefs, setPrefs] = useState(null);
  const [busy, setBusy] = useState(false);
  const [testTo, setTestTo] = useState("");
  const [testType, setTestType] = useState("test");
  const [testMsg, setTestMsg] = useState(null);
  const [sending, setSending] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [resetMsg, setResetMsg] = useState(null);

  const resetDemo = async () => {
    if (!window.confirm("Azzerare i 4 account demo (amico1–amico4)? I loro progressi e archetipi verranno cancellati.")) return;
    setResetting(true);
    setResetMsg(null);
    try {
      const { data } = await api.post("/admin/reset-demo");
      setResetMsg({ ok: true, text: `Fatto! ${data.count} account demo azzerati e pronti per una nuova prova.` });
      toast.success("Account demo azzerati");
    } catch (e) {
      setResetMsg({ ok: false, text: apiError(e.response?.data?.detail) });
      toast.error("Reset non riuscito");
    } finally {
      setResetting(false);
    }
  };

  const sendTestEmail = async () => {
    setSending(true);
    setTestMsg(null);
    try {
      const { data } = await api.post("/notifications/test-email", { recipient: testTo, template: testType });
      setTestMsg({ ok: true, text: `Inviata! (Brevo ${data.status_code}) — controlla la casella di ${testTo}.` });
      toast.success("Email di prova inviata");
    } catch (e) {
      setTestMsg({ ok: false, text: apiError(e.response?.data?.detail) });
      toast.error("Invio non riuscito");
    } finally {
      setSending(false);
    }
  };

  useEffect(() => {
    api.get("/me/settings").then(({ data }) => {
      const p = data.notif_prefs;
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      setPrefs({ ...p, timezone: p.timezone || tz });
    });
  }, []);

  if (!prefs) return <Loader />;

  const persist = async (next) => {
    setPrefs(next);
    try {
      await api.put("/me/settings", {
        push_enabled: next.push_enabled, email_enabled: next.email_enabled, promo_enabled: next.promo_enabled,
        timezone: next.timezone, times: next.times,
      });
      refreshUser();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const togglePush = async (val) => {
    if (val) {
      setBusy(true);
      try {
        await enablePush();
        await persist({ ...prefs, push_enabled: true });
        toast.success("Notifiche attivate 🔔");
      } catch (e) {
        toast.error(e.message || "Impossibile attivare le notifiche");
      } finally {
        setBusy(false);
      }
    } else {
      await disablePush();
      await persist({ ...prefs, push_enabled: false });
      toast.success("Notifiche disattivate");
    }
  };

  const setTime = (key, value) => persist({ ...prefs, times: { ...prefs.times, [key]: value } });

  const sendTest = async () => {
    try {
      await api.post("/push/test");
      toast.success("Notifica di prova inviata");
    } catch {
      toast.error("Attiva prima le notifiche");
    }
  };

  return (
    <>
    <div className="min-h-screen px-5 pb-32 pt-6" data-testid="settings-page">
      <TopLogoBar onBack={() => (window.history.state?.idx > 0 ? navigate(-1) : navigate("/profilo"))} testid="settings-back-button" />
      <div className="flex items-center gap-3">
        <h1 className="font-serif-el text-2xl font-semibold text-[#FAF9F6]">Notifiche e promemoria</h1>
      </div>

      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="mt-6 space-y-4">
        <div className="rounded-2xl border border-[#27272A] bg-[#121215] p-5" data-testid="text-size-setting">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#18181C] text-[#D4AF37]"><ALargeSmall size={18} /></span>
              <div>
                <p className="font-medium text-[#F4F4F5]">Dimensione testo</p>
                <p className="text-sm text-[#8A8E96]">Ingrandisci i testi per leggere meglio</p>
              </div>
            </div>
            <Toggle checked={big} onChange={setSize} testid="toggle-text-size" />
          </div>
        </div>

        <div className="rounded-2xl border border-[#27272A] bg-[#121215] p-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#18181C] text-[#D4AF37]"><Bell size={18} /></span>
              <div>
                <p className="font-medium text-[#F4F4F5]">Notifiche push</p>
                <p className="text-sm text-[#8A8E96]">Un avviso gentile per i 3 momenti</p>
              </div>
            </div>
            <Toggle checked={prefs.push_enabled} onChange={togglePush} testid="toggle-push" />
          </div>
          <PushHealth key={String(prefs.push_enabled)} enabled={prefs.push_enabled && !busy} className="mt-3" />
          {!pushSupported() && !prefs.push_enabled && (
            <p className="mt-3 rounded-xl border border-[#D4AF37]/20 bg-[#D4AF37]/8 p-3 text-sm text-[#D4AF37]">
              Su iPhone installa prima l'app sulla schermata Home (Condividi → Aggiungi a Home) per ricevere le notifiche.
            </p>
          )}
          {prefs.push_enabled && (
            <button onClick={sendTest} data-testid="push-test-button" className="mt-3 inline-flex items-center gap-1.5 text-base font-semibold text-[#D4AF37]">
              <Send size={14} /> Invia notifica di prova
            </button>
          )}
        </div>

        <div className="rounded-2xl border border-[#27272A] bg-[#121215] p-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#18181C] text-[#D4AF37]"><Mail size={18} /></span>
              <div>
                <p className="font-medium text-[#F4F4F5]">Email promemoria</p>
                <p className="text-sm text-[#8A8E96]">Promemoria del giorno + report finale</p>
              </div>
            </div>
            <Toggle checked={prefs.email_enabled} onChange={(v) => persist({ ...prefs, email_enabled: v })} testid="toggle-email" />
          </div>
          <div className="mt-4 flex items-center justify-between border-t border-[#27272A] pt-4">
            <div className="flex items-center gap-3">
              <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#18181C] text-[#D4AF37]"><Megaphone size={18} /></span>
              <div>
                <p className="font-medium text-[#F4F4F5]">Novità e offerte</p>
                <p className="text-sm text-[#8A8E96]">Promozioni e sconti riservati</p>
              </div>
            </div>
            <Toggle checked={prefs.promo_enabled !== false} onChange={(v) => persist({ ...prefs, promo_enabled: v })} testid="toggle-promo" />
          </div>
        </div>

        <div className="rounded-2xl border border-[#27272A] bg-[#121215] p-5">
          <div className="mb-3 flex items-center gap-2">
            <Clock size={16} className="text-[#B4B4BC]" />
            <p className="font-medium text-[#F4F4F5]">Orari dei momenti</p>
          </div>
          <div className="space-y-2.5">
            {MOMENTS.map((m) => (
              <div key={m.key} className="flex items-center justify-between rounded-xl border border-[#27272A] bg-[#0D0D0F] px-4 py-3">
                <span className="flex items-center gap-2 text-base font-medium text-[#F4F4F5]">
                  <m.icon size={16} className="text-[#D4AF37]" /> {m.label}
                </span>
                <input
                  type="time"
                  value={prefs.times[m.key]}
                  onChange={(e) => setTime(m.key, e.target.value)}
                  data-testid={`time-${m.key}`}
                  className="rounded-lg border border-[#27272A] bg-[#18181C] px-2 py-1 text-base text-[#F4F4F5] outline-none"
                />
              </div>
            ))}
          </div>
          <p className="mt-3 text-sm text-[#8A8E96]">Fuso orario: {prefs.timezone}</p>
        </div>
      </motion.div>

      {user?.role === "admin" && (
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="mt-6 rounded-3xl border border-[#D4AF37]/30 bg-[#121215] p-5" data-testid="admin-email-test">
          <div className="flex items-center gap-2 text-[#D4AF37]">
            <Mail size={16} />
            <span className="text-sm font-semibold uppercase tracking-[0.2em]">Test email (Brevo)</span>
          </div>
          <p className="mt-2 text-sm text-[#B4B4BC]">Invia un'email di prova per verificare la configurazione Brevo.</p>
          <div className="mt-3 flex gap-2">
            {[["test", "Prova"], ["weekly", "Settimanale"]].map(([v, l]) => (
              <button
                key={v}
                onClick={() => setTestType(v)}
                data-testid={`test-type-${v}`}
                className={`flex-1 rounded-full border px-3 py-2 text-sm transition ${testType === v ?"border-[#D4AF37] bg-[#D4AF37]/10 text-[#D4AF37]" : "border-[#27272A] bg-[#18181C] text-[#B4B4BC]"}`}
              >
                {l}
              </button>
            ))}
          </div>
          <input
            type="email"
            value={testTo}
            onChange={(e) => setTestTo(e.target.value)}
            placeholder="destinatario@esempio.it"
            data-testid="test-email-input"
            className="mt-3 w-full rounded-xl border border-[#27272A] bg-[#18181C] px-4 py-3 text-base text-[#F4F4F5] outline-none focus:border-[#D4AF37]/50"
          />
          <button
            onClick={sendTestEmail}
            disabled={sending || !testTo}
            data-testid="test-email-button"
            className="btn-gold mt-3 flex w-full items-center justify-center gap-2 rounded-full px-6 py-3.5 text-base uppercase tracking-wide disabled:opacity-50"
          >
            <Send size={16} /> {sending ? "Invio…" : "Invia email di prova"}
          </button>
          {testMsg && (
            <p data-testid="test-email-result" className={`mt-3 text-sm ${testMsg.ok ?"text-[#10B981]" : "text-[#F87171]"}`}>{testMsg.text}</p>
          )}
        </motion.div>
      )}

      {user?.role === "admin" && (
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="mt-4 rounded-3xl border border-[#D4AF37]/30 bg-[#121215] p-5" data-testid="admin-reset-demo">
          <div className="flex items-center gap-2 text-[#D4AF37]">
            <RotateCcw size={16} />
            <span className="text-sm font-semibold uppercase tracking-[0.2em]">Account demo amici</span>
          </div>
          <p className="mt-2 text-sm text-[#B4B4BC]">
            Azzera i 4 account di prova (amico1–amico4): rimuove archetipo e progressi così i tuoi amici ripartono dal test. Restano abbonati (nessun pagamento).
          </p>
          <button
            onClick={resetDemo}
            disabled={resetting}
            data-testid="reset-demo-button"
            className="btn-gold mt-3 flex w-full items-center justify-center gap-2 rounded-full px-6 py-3.5 text-base uppercase tracking-wide disabled:opacity-50"
          >
            <RotateCcw size={16} /> {resetting ? "Azzero…" : "Azzera account demo"}
          </button>
          {resetMsg && (
            <p data-testid="reset-demo-result" className={`mt-3 text-sm ${resetMsg.ok ?"text-[#10B981]" : "text-[#F87171]"}`}>{resetMsg.text}</p>
          )}
        </motion.div>
      )}

      {busy && <p className="mt-4 text-center text-base text-[#B4B4BC]">Attivo le notifiche…</p>}
    </div>
    <BottomTabs />
    </>
  );
}
